import React, { Fragment, useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { IoIosClose, IoMdCreate, IoIosImage } from 'react-icons/io';
import { toast } from 'react-toastify';

import FormInput from '../form/input';
import FormButton from '../form/button';
import FormCheckBox from '../form/checkbox';
import FileUpload from '../form/fileUpload';
import Thumb from '../Thumb';
import ActionButton from '../helpers/ActionButton';

const SignatureSetup = ({
  updateProfile,
  setProfile,
  setUnmountSetup,
  unmountSetup,
  setSession,
  setUnexpectedError
}) => {
  const [acceptConditionWithTerm, setAcceptConditionWithTerm] = useState(false);
  const [withText, setWithText] = useState(false);
  const [withImage, setWithImage] = useState(false);
  const [content, setContent] = useState(null);

  const TextFormSchema = Yup.object().shape({
    text: Yup.string()
      .required('*Field is required!')
      .matches(/^(?![\s.]+$)[a-zA-Z\s-_.]*$/, {
        message: '*Invalid character, Only alphaNum allowed!'
      })
      .min(5, '*Text should at least be 5 characters long!')
      .max(30, '*Text should at most be 30 characters long!')
  });

  const FileFormSchema = Yup.object().shape({
    file: Yup.mixed()
      .required('*No image file selected!')
      .test('fileSize', 'File Size is too large', value => value && value.size <= 1024 * 1024 * 1)
      .test(
        'fileType',
        'Unsupported File Format',
        value => value && ['image/jpg', 'image/jpeg', 'image/png'].includes(value.type)
      )
  });

  return (
    <Fragment>
      <div className="mt-10">
        <div className="mb-6">
          <h4 className="font-bold text-xl">Signature</h4>
          <p>Please provide a personal signature.</p>
          <p className="text-sm">
            Select either entering text(most preferred "your name") or upload an image of your
            signature in a white background less then 1mb.
          </p>
        </div>

        {withText || withImage ? (
          <div className="float-right">
            <ActionButton
              btnPad="p-2"
              color="bg-cf-300"
              name="Close"
              textColor="text-white"
              icon={<IoIosClose size={20} />}
              click={() => {
                setWithText(false);
                setWithImage(false);
                setContent(null);
                setAcceptConditionWithTerm(false);
                setUnmountSetup(!unmountSetup);
              }}
            />
          </div>
        ) : null}
        <div className="flex flex-warp justify-between relative bg-white w-full px-4 py-4 rounded">
          {!withText && !withImage ? (
            <Fragment>
              <ActionButton
                btnPad="px-6 py-3"
                color="bg-cf-300"
                name="Text"
                textColor="text-white"
                icon={<IoMdCreate size={15} />}
                click={() => setWithText(true)}
                className="mr-1"
              />
              <ActionButton
                btnPad="px-6 py-3"
                color="bg-cf-400"
                name="Image"
                textColor="text-cf-300"
                icon={<IoIosImage size={15} />}
                click={() => setWithImage(true)}
              />
            </Fragment>
          ) : (
            <Fragment>
              <div className="w-1/2">
                <Formik
                  initialValues={{ text: '', file: undefined }}
                  validationSchema={withImage ? FileFormSchema : TextFormSchema}
                  enableReinitialize
                  onSubmit={async (values, { setSubmitting }) => {
                    if (acceptConditionWithTerm) {
                      let formData = new FormData();
                      if (values.file) formData.append('signature', values.file);
                      else
                        formData.append(
                          'signature',
                          JSON.stringify({ string: values.text, check: 'text' })
                        );
                      try {
                        setSubmitting(true);
                        const response = await updateProfile(formData);
                        setSubmitting(false);
                        if (response.code === 200) {
                          setProfile(response.data);
                          toast.success(response.message, {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        } else {
                          toast.error(response.data.message, {
                            position: toast.POSITION.TOP_RIGHT
                          });
                        }
                      } catch (error) {
                        if (error && error.status === 401) setSession(false);
                        else setUnexpectedError('Unexpected Server Error!');
                      }
                    } else {
                      setSubmitting(false);
                      toast.error('You must accept term and condition!', {
                        position: toast.POSITION.TOP_RIGHT
                      });
                    }
                  }}
                >
                  {({
                    values,
                    handleSubmit,
                    handleBlur,
                    handleChange,
                    isSubmitting,
                    errors,
                    touched,
                    dirty
                  }) => (
                    <form className="flex flex-wrap mt-10" onSubmit={handleSubmit}>
                      {withImage && (
                        <Fragment>
                          <button className="focus:outline-none text-white bg-cf-300 py-3 w-full rounded inline-flex items-center">
                            <Field
                              name="file"
                              component={FileUpload}
                              accept="image/jpeg, image/png"
                              className="opacity-0 w-full cursor-pointer absolute block"
                            />
                            <span className="mx-auto">Upload your signature</span>
                          </button>
                          {setContent(values.file)}
                          {errors.file && touched.file ? (
                            <p className="text-xs text-red-700">{errors.file}</p>
                          ) : null}
                        </Fragment>
                      )}
                      {withText && (
                        <Fragment>
                          <FormInput
                            placeholder="Enter your signature"
                            className="w-full"
                            min="5"
                            max="30"
                            error={errors.text}
                            touched={touched.text}
                            inputType
                            type="text"
                            name="text"
                            change={handleChange}
                            blur={handleBlur}
                            inputValue={values.text}
                          />
                          {setContent(values.text)}
                        </Fragment>
                      )}
                      <FormButton
                        name="Submit"
                        color="bg-cf-300"
                        btnPad="py-3 text-lg"
                        className="w-full mt-4"
                        disabled={!dirty}
                        loading={isSubmitting}
                      />
                      <FormCheckBox
                        click={() => setAcceptConditionWithTerm(!acceptConditionWithTerm)}
                        checkBoxValue={acceptConditionWithTerm}
                        className="pr-2 mt-3"
                        name="acceptConditionWithTerm"
                        content={
                          <div className="ml-3 text-xs text-gray-700">
                            By clicking the "Submit" button, you accept that the signature you are
                            about to submit is binding and will serve as your signature for all
                            Complete Farmer legal document signings such as farm contracts etc.
                          </div>
                        }
                      />
                    </form>
                  )}
                </Formik>
              </div>
              <div className="flex justify-center text-center border-2 border-cf-300 mx-2 mt-10 w-1/2 signature">
                {content && (
                  <Fragment>
                    {withText && <p className="pt-4">{content}</p>}
                    {withImage && (
                      <Thumb
                        imageFile={content}
                        className={'mt-2 mb-3'}
                        style={{ width: '150px', height: '80px', padding: '10px' }}
                      />
                    )}
                  </Fragment>
                )}
              </div>
            </Fragment>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default SignatureSetup;
